import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { LoginPage } from "./auth/LoginPage.js";
import { DashboardLayout } from "./layout/DashboardLayout.js";
import { OverviewPage } from "./pages/overview/OverviewPage.js";
import { BotsPage } from "./pages/bots/BotsPage.js";
import { BotPage } from "./pages/bots/BotPage.js";
import { BillingPage } from "./pages/billing/BillingPage.js";
import { SettingsPage } from "./pages/settings/SettingsPage.js";
import { SubmissionsPage } from "./pages/submissions/SubmissionsPage.js";
import { InsightsPage } from "./pages/activity/InsightsPage.js";
import { ChatPage } from "./pages/chat/ChatPage.js";
import { EmbedPage } from "./pages/chat/EmbedPage.js";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Auth */}
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      {/* Dashboard — protected */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="bots" element={<BotsPage />} />
        <Route path="bots/:id" element={<BotPage />} />
        <Route path="submissions" element={<SubmissionsPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="billing" element={<BillingPage />} />
        {/* Billing return routes from Stripe */}
        <Route path="billing/success" element={<BillingPage />} />
        <Route path="billing/cancel" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Public chat pages — no auth */}
      <Route path="/b/:slug" element={<ChatPage />} />
      <Route path="/embed/:slug" element={<EmbedPage />} />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
